import { create } from 'zustand'

type ContactTypeFilter = 'all' | 'residential' | 'commercial' | 'business'
export type ContactWorkFilter = 'all' | 'open' | 'won' | 'closed'

interface UIState {
  sidebarCollapsed: boolean
  mobileMenuOpen: boolean
  moreMenuOpen: boolean
  activeModal: string | null
  toasts: Toast[]
  // AC-21: true once the user has interacted with the page at least once,
  // satisfying the browser autoplay policy so the inbound ring tone can play.
  // In-memory only (resets per-session on full reload).
  hasUserInteracted: boolean
  inboxBadge: number
  contactTypeFilter: ContactTypeFilter
  contactWorkFilters: Record<string, ContactWorkFilter>
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleMobileMenu: () => void
  setMoreMenuOpen: (open: boolean) => void
  openModal: (modalId: string) => void
  closeModal: () => void
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  markUserInteracted: () => void
  setInboxBadge: (count: number) => void
  setContactTypeFilter: (filter: ContactTypeFilter) => void
  setContactWorkFilter: (contactId: string, filter: ContactWorkFilter) => void
}

interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
}

let toastCounter = 0
const toastTimers = new Map<string, ReturnType<typeof setTimeout>>()

export const useUIStore = create<UIState>((set) => ({
  sidebarCollapsed: false,
  mobileMenuOpen: false,
  moreMenuOpen: false,
  activeModal: null,
  toasts: [],
  hasUserInteracted: false,
  inboxBadge: 0,
  contactTypeFilter: 'all',
  contactWorkFilters: {},
  markUserInteracted: () => set({ hasUserInteracted: true }),
  setInboxBadge: (count) => set({ inboxBadge: count }),
  setContactTypeFilter: (filter) => set({ contactTypeFilter: filter }),
  setContactWorkFilter: (contactId, filter) =>
    set((state) => ({
      contactWorkFilters: { ...state.contactWorkFilters, [contactId]: filter },
    })),
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
  toggleMobileMenu: () => set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),
  setMoreMenuOpen: (open: boolean) => set({ moreMenuOpen: open }),
  openModal: (modalId) => set({ activeModal: modalId }),
  closeModal: () => set({ activeModal: null }),
  addToast: (toast) => {
    const id = `toast-${++toastCounter}`
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }))
    const timer = setTimeout(() => {
      toastTimers.delete(id)
      set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
    }, toast.duration ?? 4000)
    toastTimers.set(id, timer)
  },
  removeToast: (id) => {
    const timer = toastTimers.get(id)
    if (timer !== undefined) {
      clearTimeout(timer)
      toastTimers.delete(id)
    }
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
  },
}))
